// opencode scheduled-task engine (ADR-0047 / ENG-7878).
//
// The claude scheduler (./scheduler/execution.ts) routes every due task through
// an in-session kanban todo card (fireScheduledTaskViaKanban): the live claude
// pane picks the card up, works it with its in-session tools, and self-completes,
// after which the manager reconciles + delivers. opencode agents have NO
// in-session kanban MCP - the manager is their sole driver over HTTP - so that
// route can't apply.
//
// Instead opencode uses the pre-ENG-6849 fire -> run -> deliver shape, with an
// HTTP inject standing in for the retired `claude -p` oneshot: when a task is
// due the manager injects its prompt into the agent's `opencode serve` (awaiting
// the turn's reply), then hands that reply to the shared delivery orchestrator
// (deliverScheduledTaskOutput) for the task's configured target. The
// schedule-state machine itself (syncTasksToScheduler / getReadyTasks /
// markTaskFired) is framework-agnostic and reused verbatim from the claude path,
// so cadence, one-shot completion, manual-trigger, and cross-restart recovery
// all behave identically - only the "how a firing runs" leaf differs.

import { createHash } from 'node:crypto';

import {
  syncTasksToScheduler,
  getReadyTasks,
  markTaskFired,
  loadSchedulerState,
  type SchedulerState,
  type SchedulerTaskInput,
  type SchedulerTaskState,
} from '../scheduler-engine.js';
import {
  injectOpencodeMessage,
  isOpencodeSessionHealthy,
} from '../opencode-session.js';
import { buildSchedulerTaskInput } from './scheduler/tasks.js';
import { KANBAN_WORK_TEMPLATES } from './kanban/parsers.js';
import { deliverScheduledTaskOutput } from './delivery/scheduled-output.js';
import { log } from './runtime.js';

// At most this many scheduled injects in flight per agent. An inject awaits the
// full agent turn (seconds to minutes), so we cap concurrency to keep one agent
// from monopolising its own serve loop; ready tasks beyond the cap are picked up
// on the next poll tick.
const MAX_OPENCODE_SCHED_CONCURRENCY = 2;

/** codeName -> the agent's cached SchedulerState (running source of truth). */
const opencodeSchedulerStates = new Map<string, SchedulerState>();
/** taskIds with an inject chain in flight, so the next tick doesn't re-fire. */
const inFlightOpencodeTasks = new Set<string>();
/** codeName -> live inject count, for the per-agent concurrency cap. */
const opencodeTaskConcurrency = new Map<string, number>();
/** agent_id -> last synced (tasks) hash, to skip re-syncing an unchanged list. */
const knownOpencodeTaskHashes = new Map<string, string>();

/** Test/diagnostic seam: forget all in-memory scheduler state for an agent. */
export function resetOpencodeSchedulerState(codeName: string, agentId: string): void {
  opencodeSchedulerStates.delete(codeName);
  opencodeTaskConcurrency.delete(codeName);
  knownOpencodeTaskHashes.delete(agentId);
}

/** Which agents currently have a scheduled inject in flight (diagnostics). */
export function activeOpencodeScheduledTasks(): string[] {
  return [...inFlightOpencodeTasks];
}

/** Test-only: forget ALL in-memory scheduler state across every agent. */
export function __resetOpencodeSchedulerForTests(): void {
  opencodeSchedulerStates.clear();
  inFlightOpencodeTasks.clear();
  opencodeTaskConcurrency.clear();
  knownOpencodeTaskHashes.clear();
}

/**
 * Whether a completed task's reply should be delivered to its configured
 * target. Mirrors the claude delivery gate (deliverScheduledCardResult):
 *  - `never`  -> the task runs but its output is not reported anywhere.
 *  - `always` / `conditional` -> deliver, provided there IS something to
 *    deliver (a non-empty reply and a resolved target). `conditional`'s
 *    "only when there's something worth sending" intent is approximated by the
 *    non-empty-reply guard - an empty turn delivers nothing under either policy.
 */
function shouldDeliver(task: SchedulerTaskState, reply: string | null): boolean {
  if ((task.deliveryPolicy ?? 'always') === 'never') return false;
  if (task.deliveryMode === 'none') return false;
  if (task.deliveryTo == null) return false;
  return typeof reply === 'string' && reply.trim().length > 0;
}

/**
 * Drive one agent's opencode scheduler for this poll tick: sync the task list
 * into the durable state, then fire every ready task via an HTTP inject whose
 * reply is delivered to the task's target.
 *
 * Never throws - a per-task failure marks that task and is logged, so one bad
 * schedule can't take down the poll loop for the rest of the fleet. Returns once
 * the ready tasks are dispatched; each firing's inject+deliver runs detached
 * (guarded by inFlightOpencodeTasks) so the poll tick is not blocked on a full
 * agent turn.
 */
export async function syncAndCheckOpencodeScheduler(
  agent: { agent_id: string; code_name: string; display_name: string },
  tasks: Array<Record<string, unknown>>,
  refreshData: Record<string, unknown>,
): Promise<void> {
  const codeName = agent.code_name;

  // Change-detection: only re-sync when the task list actually changed, matching
  // the claude path. The hash is over the raw task rows (schedule + prompt +
  // delivery), so an edit in the webapp re-syncs and a steady state is a cheap
  // no-op.
  const tasksHash = createHash('sha256')
    .update(JSON.stringify(tasks))
    .digest('hex')
    .slice(0, 16);

  if (knownOpencodeTaskHashes.get(agent.agent_id) !== tasksHash) {
    const taskInputs: SchedulerTaskInput[] = tasks.map((t) => buildSchedulerTaskInput(t));
    const state = syncTasksToScheduler(codeName, agent.agent_id, taskInputs);
    opencodeSchedulerStates.set(codeName, state);
    knownOpencodeTaskHashes.set(agent.agent_id, tasksHash);
    log(`[opencode-scheduler] tasks synced for '${codeName}' (${taskInputs.length} task(s))`);
  }

  if (!opencodeSchedulerStates.has(codeName)) {
    opencodeSchedulerStates.set(codeName, loadSchedulerState(codeName));
  }

  const state = opencodeSchedulerStates.get(codeName)!;
  const ready = getReadyTasks(state, inFlightOpencodeTasks);
  if (ready.length === 0) return;

  // No live serve to inject into: leave the ready tasks untouched (no fire, no
  // schedule advance) so they run once the server is healthy, rather than
  // burning the firing against a declined inject. ensureOpencodeRuntime spawns
  // the server on the session tick; this is at most a one-poll delay after boot.
  if (!isOpencodeSessionHealthy(codeName)) {
    log(`[opencode-scheduler] '${codeName}' has ${ready.length} due task(s) but no healthy serve yet - deferring`);
    return;
  }

  for (const task of ready) {
    if ((opencodeTaskConcurrency.get(codeName) ?? 0) >= MAX_OPENCODE_SCHED_CONCURRENCY) break;

    // kanban-work is an in-session board-driven loop opencode doesn't have; the
    // claude path skips it defensively and so do we - advance the schedule so it
    // isn't re-detected every tick, but never inject it.
    if (KANBAN_WORK_TEMPLATES.has(task.templateId)) {
      opencodeSchedulerStates.set(codeName, markTaskFired(codeName, task.taskId, 'ok'));
      continue;
    }

    inFlightOpencodeTasks.add(task.taskId);
    opencodeTaskConcurrency.set(codeName, (opencodeTaskConcurrency.get(codeName) ?? 0) + 1);

    // Detached: the inject awaits a full agent turn. Guarded by
    // inFlightOpencodeTasks (added synchronously above) so the next poll tick
    // won't re-fire this task before markTaskFired advances its schedule.
    void fireOpencodeScheduledTask(agent, task).finally(() => {
      inFlightOpencodeTasks.delete(task.taskId);
      opencodeTaskConcurrency.set(
        codeName,
        Math.max(0, (opencodeTaskConcurrency.get(codeName) ?? 1) - 1),
      );
    });
  }
}

/**
 * Run one due task: inject its prompt into `opencode serve`, await the reply,
 * deliver it (when the policy + target allow), and advance the schedule.
 * Self-contained error handling - the caller only clears the in-flight guard.
 */
async function fireOpencodeScheduledTask(
  agent: { agent_id: string; code_name: string },
  task: SchedulerTaskState,
): Promise<void> {
  const codeName = agent.code_name;
  // Unique per firing so each scheduled run gets a clean opencode session (the
  // isolated-context semantics the retired `claude -p` oneshot had), rather than
  // threading onto a prior run's conversation.
  const conversationKey = `scheduled:${task.taskId}:${Date.now()}`;

  try {
    const result = await injectOpencodeMessage(
      codeName,
      {
        channelId: 'scheduler',
        conversationKey,
        senderId: 'scheduler',
        text: task.prompt,
        meta: {
          source: 'scheduled_task',
          task: task.name,
          // The manager delivers the output; the agent isn't replying into a
          // channel thread itself, so it must not try to.
          requires_reply: 'false',
        },
      },
      { awaitReply: true },
    );

    if (result.status === 'declined') {
      // server_not_running here means the serve died between the health check and
      // the inject. Don't advance the schedule - retry on the next tick.
      log(`[opencode-scheduler] task '${task.name}' for '${codeName}' declined: ${result.reason}`);
      return;
    }

    const reply = result.status === 'replied' ? result.reply : null;

    if (shouldDeliver(task, reply)) {
      try {
        await deliverScheduledTaskOutput(codeName, agent.agent_id, task.deliveryTo, reply!, task.taskId);
      } catch (err) {
        log(`[opencode-scheduler] delivery failed for task '${task.name}' on '${codeName}': ${(err as Error).message}`);
      }
    } else if ((task.deliveryPolicy ?? 'always') !== 'never' && task.deliveryTo != null) {
      // Ran, but produced nothing to send (empty turn). Not an error - the
      // schedule still advances so we don't re-fire.
      log(`[opencode-scheduler] task '${task.name}' for '${codeName}' produced no output to deliver`);
    }

    opencodeSchedulerStates.set(codeName, markTaskFired(codeName, task.taskId, 'ok'));
  } catch (err) {
    // The inject threw after (or during) a durable admit - the turn may already
    // be running in the session. Advance the schedule anyway: re-firing would
    // double-inject the same prompt (InboundError.admitted), and at-most-once is
    // the safer failure for a scheduled task than a duplicate run.
    log(`[opencode-scheduler] task '${task.name}' for '${codeName}' failed: ${(err as Error).message}`);
    opencodeSchedulerStates.set(codeName, markTaskFired(codeName, task.taskId, 'error'));
  }
}
